/* eslint-disable react-hooks/set-state-in-effect, react-hooks/immutability */
import React, { useEffect, useState, useSyncExternalStore } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Trash2, Download, BarChart3, Users, Ticket, Clock, Trophy, X, Sparkles, FileText, MessageCircle, Building2 } from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';
import { ADMIN } from '@/constants/testIds';
import { PRODUCTS } from '@/data/products';
import JellyLogo from '@/components/JellyLogo';
import WholesaleList from '@/components/WholesaleList';
import InventoryPanel from '@/components/InventoryPanel';
// Lazy-load the heavy Recharts dashboard so the first paint on mobile is instant.
// The login screen + leads table render without recharts in the initial bundle.
const AdminDashboard = React.lazy(() => import('@/components/AdminDashboard'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// External store for the active raffle schedule, polled every 5s while a token exists.
// Uses useSyncExternalStore (React 18+) — the idiomatic way to subscribe to external,
// mutable data without violating react-hooks/set-state-in-effect rules.
const _scheduleStore = (() => {
  let value = null;
  const listeners = new Set();
  let pollHandle = null;
  let activeToken = '';
  const notify = () => listeners.forEach((l) => l());
  const fetchOnce = async () => {
    if (!activeToken) return;
    try {
      const { data } = await axios.get(`${API}/admin/raffle/schedule`, {
        headers: { 'X-Admin-Token': activeToken },
      });
      const next = data && data.scheduled_at ? data : null;
      if (JSON.stringify(next) !== JSON.stringify(value)) {
        value = next;
        notify();
      }
    } catch (err) {
      console.error('[Admin] schedule poll failed:', err?.message || err);
    }
  };
  return {
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
        if (listeners.size === 0 && pollHandle) {
          clearInterval(pollHandle);
          pollHandle = null;
        }
      };
    },
    getSnapshot() { return value; },
    setToken(token) {
      if (token === activeToken) return;
      activeToken = token;
      if (pollHandle) { clearInterval(pollHandle); pollHandle = null; }
      if (token) {
        fetchOnce();
        pollHandle = setInterval(fetchOnce, 5000);
      } else {
        value = null;
        notify();
      }
    },
    setLocal(next) {
      value = next;
      notify();
    },
  };
})();

const REG_STATUS_COLORS = {
  new: 'bg-[#FFE6EB] text-[#FF5C83]',
  contacted: 'bg-[#F1ECF9] text-[#7C5DA5]',
  won: 'bg-[#FAEFC2] text-[#9C7A20]',
  discarded: 'bg-[#1F1A24]/10 text-[#1F1A24]',
};
const DIST_STATUS_COLORS = {
  new: 'bg-[#FFE6EB] text-[#FF5C83]',
  contacted: 'bg-[#F1ECF9] text-[#7C5DA5]',
  approved: 'bg-[#E5F8EA] text-[#1E8A4A]',
  rejected: 'bg-[#1F1A24]/10 text-[#1F1A24]',
};

const productName = (slug, lang) => {
  const p = PRODUCTS.find((x) => x.slug === slug);
  return p ? p.name[lang] : slug;
};

export default function Admin() {
  const { t, lang } = useLang();
  const [token, setToken] = useState(() => localStorage.getItem('jelly_admin_token') || '');
  const [password, setPassword] = useState('');
  const [tab, setTab] = useState('registrations');
  const [registrations, setRegistrations] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [stats, setStats] = useState(null);
  const [regStats, setRegStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const schedule = useSyncExternalStore(_scheduleStore.subscribe, _scheduleStore.getSnapshot);
  const [scheduleInput, setScheduleInput] = useState('');
  // Filters
  const [filterCity, setFilterCity] = useState('');
  const [filterTier, setFilterTier] = useState('');
  // Distinct cities for the city filter dropdown
  const [cityOptions, setCityOptions] = useState([]);
  // Live counter (polled every 5s)
  const [liveCount, setLiveCount] = useState(null);

  const headers = { 'X-Admin-Token': token };
  const isAuthed = !!token;

  // Drive the schedule store from the token. Effect only updates the store, no setState here.
  useEffect(() => {
    _scheduleStore.setToken(token);
  }, [token]);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCity) params.set('city', filterCity);
      if (filterTier) params.set('tier', filterTier);
      const qs = params.toString();
      const [r, d, s, rs, sch, cities] = await Promise.all([
        axios.get(`${API}/admin/registrations${qs ? `?${qs}` : ''}`, { headers }),
        axios.get(`${API}/admin/distributors`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/registrations/stats`, { headers }),
        axios.get(`${API}/admin/raffle/schedule`, { headers }),
        axios.get(`${API}/admin/registrations/cities`, { headers }),
      ]);
      setRegistrations(r.data);
      setDistributors(d.data);
      setStats(s.data);
      setRegStats(rs.data);
      setCityOptions(cities.data || []);
      _scheduleStore.setLocal(sch.data && sch.data.scheduled_at ? sch.data : null);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        toast.error('Sesión expirada');
      } else {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token, filterCity, filterTier]);

  // Live counter polling (lightweight)
  useEffect(() => {
    if (!token) {
      setLiveCount(null);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/registrations/live-count`, { headers });
        if (!cancelled) setLiveCount(data);
      } catch (err) {
        // silent — polling
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  const scheduleRaffle = async (isoDate, winnersCount = 1) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/raffle/schedule`,
        { scheduled_at: isoDate, winners_count: winnersCount },
        { headers }
      );
      _scheduleStore.setLocal(data);
      toast.success(lang === 'es' ? 'Sorteo programado 🎉' : 'Raffle scheduled 🎉');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const cancelSchedule = async () => {
    if (!window.confirm(lang === 'es' ? '¿Cancelar el sorteo programado?' : 'Cancel scheduled raffle?')) return;
    try {
      await axios.delete(`${API}/admin/raffle/schedule`, { headers });
      _scheduleStore.setLocal(null);
      toast.success(lang === 'es' ? 'Sorteo cancelado' : 'Raffle cancelled');
    } catch {
      toast.error('Error');
    }
  };

  const drawNow = async (winnersCount = 1, isSpecial = false) => {
    const msg = isSpecial
      ? (lang === 'es'
          ? `¿Ejecutar SORTEO ESPECIAL Expobelleza con ${winnersCount} ganadoras? (Día 12 + Día 13 combinado)`
          : `Run SPECIAL Expobelleza raffle with ${winnersCount} winners? (Day 12 + Day 13 combined)`)
      : (lang === 'es'
          ? '¿Sortear AHORA mismo? Se elegirá una ganadora al azar y aparecerá en /sorteo con confeti.'
          : 'Draw the raffle RIGHT NOW? A random winner will be picked and shown on /sorteo with confetti.');
    if (!window.confirm(msg)) return;
    try {
      const eventLabel = 'Expobelleza 2026';
      const { data } = await axios.post(
        `${API}/admin/raffle/draw-now?winners_count=${winnersCount}&event=${encodeURIComponent(eventLabel)}`,
        null,
        { headers }
      );
      const winners = data?.winners || [];
      if (winners.length === 1) {
        const w = winners[0];
        toast.success(
          (lang === 'es' ? '🎉 Ganadora: ' : '🎉 Winner: ') +
          (w ? `${w.first_name}${w.city ? ' · ' + w.city : ''}` : '')
        );
      } else {
        toast.success(
          (lang === 'es' ? `🎉 ${winners.length} ganadoras sorteadas` : `🎉 ${winners.length} winners drawn`),
          { description: winners.map((w) => `${w.first_name}${w.city ? ' · ' + w.city : ''}`).join(', ') }
        );
      }
      // Refresh schedule + registrations to show updated status
      const sch = await axios.get(`${API}/admin/raffle/schedule`, { headers });
      _scheduleStore.setLocal(sch.data && sch.data.scheduled_at ? sch.data : null);
      const r = await axios.get(`${API}/admin/registrations`, { headers });
      setRegistrations(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 404) {
        toast.error(lang === 'es' ? 'No hay participantes elegibles' : 'No eligible participants');
      } else {
        toast.error(detail || 'Error');
      }
    }
  };

  const scheduleTonightAt9 = () => {
    // Target: today 21:00 Colombia time (UTC-5). If already past, schedule for tomorrow.
    const colombiaOffsetMs = -5 * 60 * 60 * 1000;
    const nowUtc = Date.now();
    const colombiaNow = new Date(nowUtc + colombiaOffsetMs);
    const y = colombiaNow.getUTCFullYear();
    const m = colombiaNow.getUTCMonth();
    const d = colombiaNow.getUTCDate();
    // 21:00 Colombia == 02:00 UTC next day. Date.UTC handles hour overflow → next day.
    let target = Date.UTC(y, m, d, 26, 0, 0);
    if (target <= nowUtc) target += 24 * 60 * 60 * 1000;
    scheduleRaffle(new Date(target).toISOString(), 1);
  };

  const submitCustomSchedule = (e) => {
    e.preventDefault();
    if (!scheduleInput) return;
    // datetime-local returns local time without TZ. Convert via Date to ISO UTC.
    const localDate = new Date(scheduleInput);
    if (isNaN(localDate.getTime())) {
      toast.error('Fecha inválida');
      return;
    }
    if (localDate.getTime() <= Date.now()) {
      toast.error(lang === 'es' ? 'La fecha debe ser futura' : 'Date must be in the future');
      return;
    }
    scheduleRaffle(localDate.toISOString(), 1);
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('jelly_admin_token', data.token);
      setToken(data.token);
      toast.success('OK');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const logout = () => {
    localStorage.removeItem('jelly_admin_token');
    setToken('');
    setRegistrations([]);
    setDistributors([]);
    setStats(null);
    setRegStats(null);
  };

  const updateRegStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/admin/registrations/${id}`, { status }, { headers });
      const updated = { ...res.data };
      setRegistrations((arr) => arr.map((it) => (it.id === id ? updated : it)));
    } catch (err) {
      toast.error('No se pudo actualizar');
    }
  };

  const toggleTag = async (id, tag, currentlyOn) => {
    try {
      const res = await axios.patch(
        `${API}/admin/registrations/${id}/tag`,
        { tag, value: !currentlyOn },
        { headers }
      );
      setRegistrations((arr) => arr.map((it) => (it.id === id ? res.data : it)));
    } catch (err) {
      toast.error('No se pudo actualizar la etiqueta');
    }
  };

  const buildContactNowLink = (r) => {
    const digits = String(r.phone || '').replace(/\D/g, '');
    const normalized = digits.length === 10 ? `57${digits}` : digits;
    const first = (r.name || '').split(/\s+/)[0] || '';
    const text = lang === 'es'
      ? `Hola ${first}! 💕 Soy del equipo de Jelly by Enkor 🍓 Vi que te registraste en Expobelleza 2026 — quería darte la bienvenida y contarte que tenemos un regalito especial para ti. ¿Tienes 2 minutos? ✨`
      : `Hi ${first}! 💕 I'm from the Jelly by Enkor team 🍓 I saw you registered at Expobelleza 2026 — I wanted to welcome you personally and let you know we have a little gift for you. Do you have 2 minutes? ✨`;
    return `https://wa.me/${normalized}?text=${encodeURIComponent(text)}`;
  };
  const updateDistStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/admin/distributors/${id}`, { status }, { headers });
      const updated = { ...res.data };
      setDistributors((arr) => arr.map((it) => (it.id === id ? updated : it)));
    } catch (err) {
      toast.error('No se pudo actualizar');
    }
  };
  const removeReg = async (id) => {
    if (!window.confirm(t('admin.confirmDelete'))) return;
    try {
      await axios.delete(`${API}/admin/registrations/${id}`, { headers });
      setRegistrations((arr) => arr.filter((it) => it.id !== id));
    } catch { toast.error('Error'); }
  };
  const removeDist = async (id) => {
    if (!window.confirm(t('admin.confirmDelete'))) return;
    try {
      await axios.delete(`${API}/admin/distributors/${id}`, { headers });
      setDistributors((arr) => arr.filter((it) => it.id !== id));
    } catch { toast.error('Error'); }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/registrations/export`, {
        headers,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `jelly_registrations_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Error al exportar');
    }
  };

  const exportPdf = async () => {
    try {
      const res = await axios.get(`${API}/admin/registrations/export.pdf`, {
        headers,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Jelly_Inscritas_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Error al generar PDF');
    }
  };

  const exportXlsx = async () => {
    try {
      const res = await axios.get(`${API}/admin/registrations/export.xlsx`, {
        headers,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Jelly_Inscritas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Error al generar Excel');
    }
  };

  const exportWhatsApp = async () => {
    try {
      const res = await axios.get(`${API}/admin/registrations/export/whatsapp`, {
        headers,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Jelly_WhatsApp_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(lang === 'es' ? 'Lista WhatsApp lista' : 'WhatsApp list ready');
    } catch (err) {
      toast.error('Error');
    }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 py-16 bg-[#FFFBFB]">
        <form
          data-testid={ADMIN.loginForm}
          onSubmit={login}
          className="w-full max-w-md rounded-[2rem] bg-white border border-[#A99BE0]/20 p-8 md:p-10 shadow-[0_24px_60px_rgba(169,155,224,0.12)]"
        >
          <JellyLogo size="lg" color="#FF2D9C" bylineColor="#FF2D9C" />
          <h1 className="mt-6 font-display text-3xl font-black tracking-tighter">{t('admin.title')}</h1>
          <p className="mt-2 text-sm text-[#574A66]">{t('admin.subtitle')}</p>
          <input
            data-testid={ADMIN.loginPassword}
            type="password"
            placeholder={t('admin.passwordPh')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-8 w-full rounded-2xl border border-[#A99BE0]/30 bg-[#FFFBFB] px-4 py-3 text-sm focus:outline-none focus:border-[#A99BE0] focus:ring-2 focus:ring-[#A99BE0]/20"
            required
          />
          <button
            data-testid={ADMIN.loginSubmit}
            type="submit"
            className="mt-4 w-full inline-flex justify-center items-center px-6 py-3 rounded-full bg-[#FF7597] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#FF5C83] transition-colors"
          >
            {t('admin.login')}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-[#FFFBFB] min-h-screen px-4 md:px-10 lg:px-16 py-10">
      <div className="max-w-7xl mx-auto" data-testid={ADMIN.dashboard}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#A99BE0]">{t('admin.title')}</p>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">{t('admin.subtitle')}</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid={ADMIN.exportCsv}
              onClick={exportCsv}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#F4DD8C] text-[#1F1A24] text-xs font-bold tracking-widest uppercase hover:bg-[#FAEFC2] transition-colors"
            >
              <Download className="w-4 h-4" /> {t('admin.exportCsv')}
            </button>
            <button
              data-testid="admin-export-pdf"
              onClick={exportPdf}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#FF7597] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#FF5C83] transition-colors shadow-[0_8px_24px_rgba(255,117,151,0.30)]"
            >
              <FileText className="w-4 h-4" /> PDF
            </button>
            <button
              data-testid="admin-export-xlsx"
              onClick={exportXlsx}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1E8A4A] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#256E45] transition-colors"
            >
              <Download className="w-4 h-4" /> Excel
            </button>
            <button
              data-testid="admin-export-whatsapp"
              onClick={exportWhatsApp}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#1FA957] transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </button>
            <button
              data-testid={ADMIN.logout}
              onClick={logout}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1F1A24] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#FF7597] transition-colors"
            >
              <LogOut className="w-4 h-4" /> {t('admin.logout')}
            </button>
          </div>
        </div>

        {/* LIVE COUNTER (real-time polling) */}
        <LiveCounter data={liveCount} lang={lang} />

        {/* STATS */}
        {regStats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label={t('admin.stats.registrations')} value={regStats.total} accent />
            <StatCard label={t('admin.stats.wholesale')} value={regStats.wholesale} />
            <StatCard label={t('admin.stats.retail')} value={regStats.retail} />
            <StatCard label={t('admin.stats.raffle')} value={regStats.raffle_participants} />
          </div>
        )}

        {/* DASHBOARD CHARTS · lazy-loaded to keep first paint snappy on mobile */}
        <React.Suspense
          fallback={
            <div className="mt-6 rounded-3xl bg-white border border-[#A99BE0]/10 p-5 text-xs text-[#574A66]">
              {lang === 'es' ? 'Cargando gráficas…' : 'Loading charts…'}
            </div>
          }
        >
          <AdminDashboard token={token} lang={lang} />
        </React.Suspense>

        {/* TOP MAYORISTAS / DISTRIBUIDORES */}
        <div className="mt-6 rounded-3xl bg-white border border-[#A99BE0]/20 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-[#FF2D9C]" />
            <h2 className="font-display text-xl font-black tracking-tighter">
              {lang === 'es' ? 'Top mayoristas / distribuidores' : 'Top wholesale / distributors'}
            </h2>
            <span className="text-[10px] tracking-widest uppercase font-bold px-2 py-0.5 rounded-full bg-[#FF2D9C] text-white">
              🔥 HOT
            </span>
          </div>
          <WholesaleList token={token} lang={lang} />

          {/* INVENTORY · 100 units per SKU · manual decrement */}
          <InventoryPanel token={token} lang={lang} />
        </div>

        {/* RAFFLE SCHEDULER */}
        <RaffleScheduleCard
          schedule={schedule}
          onScheduleTonight={scheduleTonightAt9}
          onCustomSubmit={submitCustomSchedule}
          scheduleInput={scheduleInput}
          setScheduleInput={setScheduleInput}
          onCancel={cancelSchedule}
          onDrawNow={() => drawNow(1, false)}
          onDrawSpecial={() => drawNow(6, true)}
          lang={lang}
        />

        {/* BREAKDOWN CARDS */}
        {regStats && tab === 'registrations' && (
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <BreakdownCard
              icon={<BarChart3 className="w-4 h-4 text-[#A99BE0]" />}
              title={t('admin.breakdown.cities')}
              items={regStats.by_city}
            />
            <BreakdownCard
              icon={<Ticket className="w-4 h-4 text-[#F4DD8C]" />}
              title={t('admin.breakdown.favorites')}
              items={(regStats.by_favorite_product || []).map((it) => ({
                ...it,
                key: productName(it.key, lang),
              }))}
            />
          </div>
        )}

        {/* TABS */}
        <div className="mt-8 inline-flex gap-1 rounded-full bg-white border border-[#A99BE0]/20 p-1 shadow-sm">
          {[
            { id: 'registrations', label: t('admin.tabs.registrations'), tid: ADMIN.tabRegistrations },
            { id: 'distributors', label: t('admin.tabs.distributors'), tid: ADMIN.tabDistributors },
          ].map((tt) => (
            <button
              key={tt.id}
              data-testid={tt.tid}
              onClick={() => setTab(tt.id)}
              className={`px-5 py-2 rounded-full text-xs font-bold tracking-widest uppercase transition-colors ${
                tab === tt.id ? 'bg-[#1F1A24] text-white' : 'text-[#1F1A24] hover:text-[#FF7597]'
              }`}
            >
              {tt.label}
            </button>
          ))}
        </div>

        {/* FILTERS · only on registrations tab */}
        {tab === 'registrations' && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="admin-filters">
            <span className="text-[10px] tracking-widest uppercase font-bold text-[#574A66]">
              {lang === 'es' ? 'Filtros' : 'Filters'}:
            </span>
            {/* Tier filter pills */}
            <div className="inline-flex gap-1 rounded-full bg-white border border-[#A99BE0]/20 p-1">
              {[
                { v: '', l: lang === 'es' ? 'Todos' : 'All' },
                { v: 'hot', l: '🔥 HOT' },
                { v: 'warm', l: '💎 WARM' },
                { v: 'cold', l: '💕 COLD' },
              ].map((opt) => (
                <button
                  key={opt.v || 'all'}
                  data-testid={`filter-tier-${opt.v || 'all'}`}
                  onClick={() => setFilterTier(opt.v)}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase transition-colors ${
                    filterTier === opt.v ? 'bg-[#FF7597] text-white' : 'text-[#1F1A24] hover:bg-[#FFE6EB]'
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            {/* City filter dropdown */}
            <select
              data-testid="filter-city"
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              className="text-xs rounded-full border border-[#A99BE0]/30 bg-white px-3 py-2 font-bold tracking-widest uppercase text-[#1F1A24] focus:outline-none focus:border-[#A99BE0]"
            >
              <option value="">{lang === 'es' ? '📍 Todas las ciudades' : '📍 All cities'}</option>
              {cityOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {(filterCity || filterTier) && (
              <button
                data-testid="filter-clear"
                onClick={() => { setFilterCity(''); setFilterTier(''); }}
                className="text-[10px] font-bold tracking-widest uppercase text-[#FF2D9C] hover:underline"
              >
                {lang === 'es' ? 'Limpiar' : 'Clear'}
              </button>
            )}
          </div>
        )}

        {/* TABLES */}
        <div className="mt-6 rounded-3xl bg-white border border-[#A99BE0]/10 overflow-hidden shadow-[0_8px_32px_rgba(169,155,224,0.08)]">
          {loading ? (
            <p className="p-8 text-sm text-[#574A66]">{t('common.loading')}</p>
          ) : tab === 'registrations' ? (
            registrations.length === 0 ? (
              <p className="p-8 text-sm text-[#574A66]">{t('admin.empty')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F1ECF9] text-[10px] font-bold tracking-[0.2em] uppercase text-[#574A66]">
                    <tr>
                      <th className="text-left px-4 py-4">{t('registration.name')}</th>
                      <th className="text-left px-4 py-4">{lang === 'es' ? 'Score · Tier' : 'Score · Tier'}</th>
                      <th className="text-left px-4 py-4">{t('registration.phone')}</th>
                      <th className="text-left px-4 py-4">{t('registration.city')}</th>
                      <th className="text-left px-4 py-4">{lang === 'es' ? 'Etiquetas' : 'Tags'}</th>
                      <th className="text-left px-4 py-4">{t('registration.favoriteProduct')}</th>
                      <th className="text-left px-4 py-4">Status</th>
                      <th className="text-left px-4 py-4">{lang === 'es' ? 'Acción' : 'Action'}</th>
                      <th className="px-4 py-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((it) => (
                      <tr key={it.id} data-testid={ADMIN.registrationRow(it.id)} className="border-t border-[#A99BE0]/10 align-top">
                        <td className="px-4 py-4 font-semibold text-[#1F1A24]">
                          {it.name}
                          <div className="text-[10px] text-[#574A66] mt-1">
                            {new Date(it.created_at).toLocaleString(lang === 'es' ? 'es-CO' : 'en-US')}
                          </div>
                          {it.stand_code && (
                            <div className="text-[10px] text-[#A99BE0] mt-1 font-bold tracking-widest">
                              📍 STAND {it.stand_code}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-1">
                            <TierBadge tier={it.tier} />
                            <span data-testid={`score-${it.id}`} className="text-[11px] font-display font-black text-[#FF2D9C] tabular-nums">
                              {typeof it.lead_score === 'number' ? it.lead_score : 0} pts
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-[#574A66]">
                          {it.phone}
                          {it.email && <div className="text-[11px] text-[#A99BE0] break-all">{it.email}</div>}
                        </td>
                        <td className="px-4 py-4">{it.city}</td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1.5 min-w-[180px]">
                            <TagPill
                              testId={`tag-visited-${it.id}`}
                              active={(it.tags || []).includes('VISITO_STAND')}
                              onClick={() => toggleTag(it.id, 'VISITO_STAND', (it.tags || []).includes('VISITO_STAND'))}
                              label={lang === 'es' ? '🎀 Visitó stand' : '🎀 Visited stand'}
                              color="violet"
                            />
                            <TagPill
                              testId={`tag-distrib-${it.id}`}
                              active={(it.tags || []).includes('INTERESADO_DISTRIBUCION')}
                              onClick={() => toggleTag(it.id, 'INTERESADO_DISTRIBUCION', (it.tags || []).includes('INTERESADO_DISTRIBUCION'))}
                              label={lang === 'es' ? '🚚 Interés distribución' : '🚚 Distribution'}
                              color="pink"
                            />
                            <TagPill
                              testId={`tag-mayorista-${it.id}`}
                              active={(it.tags || []).includes('COMPRA_MAYORISTA')}
                              onClick={() => toggleTag(it.id, 'COMPRA_MAYORISTA', (it.tags || []).includes('COMPRA_MAYORISTA'))}
                              label={lang === 'es' ? '📦 Mayorista' : '📦 Wholesale'}
                              color="gold"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-4">{it.favorite_product ? productName(it.favorite_product, lang) : '—'}</td>
                        <td className="px-4 py-4">
                          <select
                            data-testid={ADMIN.statusSelect(it.id)}
                            value={it.status}
                            onChange={(e) => updateRegStatus(it.id, e.target.value)}
                            className={`text-[11px] font-bold tracking-widest uppercase rounded-full px-3 py-2 border-0 outline-none ${REG_STATUS_COLORS[it.status] || ''}`}
                          >
                            {['new', 'contacted', 'won', 'discarded'].map((s) => (
                              <option key={s} value={s}>{t(`admin.status.${s}`)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-4">
                          {it.phone && (
                            <a
                              href={buildContactNowLink(it)}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`contact-now-${it.id}`}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase whitespace-nowrap transition-transform hover:scale-105 ${
                                it.tier === 'hot'
                                  ? 'bg-[#25D366] text-white shadow-[0_6px_16px_rgba(37,211,102,0.35)] animate-pulse'
                                  : it.tier === 'warm'
                                  ? 'bg-[#F4DD8C] text-[#1F1A24]'
                                  : 'bg-[#F0EBFC] text-[#7C5DA5]'
                              }`}
                              title={lang === 'es' ? 'Abrir WhatsApp con plantilla' : 'Open WhatsApp with template'}
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                              {lang === 'es' ? 'Contactar' : 'Contact'}
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <button
                            data-testid={ADMIN.deleteBtn(it.id)}
                            onClick={() => removeReg(it.id)}
                            className="inline-flex items-center gap-1 text-[#1F1A24]/70 hover:text-red-500"
                            aria-label="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : distributors.length === 0 ? (
            <p className="p-8 text-sm text-[#574A66]">{t('admin.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FFE6EB] text-[10px] font-bold tracking-[0.2em] uppercase text-[#574A66]">
                  <tr>
                    <th className="text-left px-4 py-4">{t('distributors.businessName')}</th>
                    <th className="text-left px-4 py-4">{t('distributors.contactName')}</th>
                    <th className="text-left px-4 py-4">{t('distributors.email')}</th>
                    <th className="text-left px-4 py-4">{t('distributors.country')}</th>
                    <th className="text-left px-4 py-4">{t('distributors.businessType')}</th>
                    <th className="text-left px-4 py-4">{t('distributors.monthlyVolume')}</th>
                    <th className="text-left px-4 py-4">Status</th>
                    <th className="text-right px-4 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {distributors.map((it) => (
                    <tr key={it.id} data-testid={ADMIN.distributorRow(it.id)} className="border-t border-[#FF7597]/10 align-top">
                      <td className="px-4 py-4 font-semibold text-[#1F1A24]">
                        {it.business_name}
                        <div className="text-[10px] text-[#574A66] mt-1">
                          {new Date(it.created_at).toLocaleString(lang === 'es' ? 'es-CO' : 'en-US')}
                        </div>
                      </td>
                      <td className="px-4 py-4">{it.contact_name}<div className="text-[11px] text-[#574A66]">{it.phone}</div></td>
                      <td className="px-4 py-4 text-[#574A66] break-all">{it.email}</td>
                      <td className="px-4 py-4">{it.city}, {it.country}</td>
                      <td className="px-4 py-4">{it.business_type}</td>
                      <td className="px-4 py-4">{it.monthly_volume}</td>
                      <td className="px-4 py-4">
                        <select
                          value={it.status}
                          onChange={(e) => updateDistStatus(it.id, e.target.value)}
                          className={`text-[11px] font-bold tracking-widest uppercase rounded-full px-3 py-2 border-0 outline-none ${DIST_STATUS_COLORS[it.status] || ''}`}
                        >
                          {['new', 'contacted', 'approved', 'rejected'].map((s) => (
                            <option key={s} value={s}>{t(`admin.status.${s}`)}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={() => removeDist(it.id)}
                          className="inline-flex items-center gap-1 text-[#1F1A24]/70 hover:text-red-500"
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


const TIER_BADGE_STYLES = {
  hot: { label: '🔥 HOT', cls: 'bg-[#FF2D9C] text-white' },
  warm: { label: '💎 WARM', cls: 'bg-[#F4DD8C] text-[#1F1A24]' },
  cold: { label: '💕 COLD', cls: 'bg-[#F0EBFC] text-[#A99BE0]' },
};

const TierBadge = ({ tier }) => {
  const meta = TIER_BADGE_STYLES[(tier || 'cold').toLowerCase()] || TIER_BADGE_STYLES.cold;
  return (
    <span
      data-testid={`tier-${tier || 'cold'}`}
      className={`inline-block text-[9px] font-bold tracking-widest uppercase rounded-full px-2.5 py-1 whitespace-nowrap ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
};

const TAG_PILL_STYLES = {
  violet: { active: 'bg-[#A99BE0] text-white', idle: 'bg-[#F0EBFC] text-[#7C5DA5] hover:bg-[#E5DEF7]' },
  pink:   { active: 'bg-[#FF2D9C] text-white', idle: 'bg-[#FFE6EB] text-[#FF2D9C] hover:bg-[#FFD0DC]' },
  gold:   { active: 'bg-[#F4A300] text-white', idle: 'bg-[#FAEFC2] text-[#9C7A20] hover:bg-[#F4DD8C]' },
};

const TagPill = ({ testId, active, onClick, label, color = 'violet' }) => {
  const meta = TAG_PILL_STYLES[color] || TAG_PILL_STYLES.violet;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-[9px] font-bold tracking-widest uppercase rounded-full px-2 py-1 transition-colors whitespace-nowrap ${
        active ? meta.active : meta.idle
      }`}
    >
      {label}
    </button>
  );
};

const LiveCounter = ({ data, lang }) => {
  if (!data) return null;
  return (
    <div
      data-testid="live-counter"
      className="mt-6 rounded-3xl bg-gradient-to-r from-[#1F1A24] via-[#7C5DA5] to-[#A99BE0] p-[2px] shadow-[0_12px_40px_rgba(124,93,165,0.25)]"
    >
      <div className="rounded-[1.4rem] bg-[#1F1A24] px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#FF2D9C] opacity-75 animate-ping"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#FF2D9C]"></span>
          </span>
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/70">
            {lang === 'es' ? 'EN VIVO · INSCRIPCIONES' : 'LIVE · REGISTRATIONS'}
          </p>
        </div>
        <div className="flex items-baseline gap-6 flex-wrap">
          <p data-testid="live-total" className="font-display text-3xl md:text-4xl font-black tracking-tighter text-white tabular-nums">
            {data.total}
          </p>
          <div className="flex items-center gap-3 text-[11px] font-bold tracking-widest uppercase">
            <span className="text-[#FF7597]">🔥 {data.hot} HOT</span>
            <span className="text-[#F4DD8C]">💎 {data.warm} WARM</span>
            <span className="text-[#E5DEF7]">💕 {data.cold} COLD</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent }) => (
  <div className={`rounded-3xl border p-5 ${accent ? 'bg-[#A99BE0] text-white border-transparent' : 'bg-white border-[#A99BE0]/10 text-[#1F1A24]'}`}>
    <p className={`text-[10px] font-bold tracking-[0.25em] uppercase ${accent ? 'text-white/80' : 'text-[#574A66]'}`}>{label}</p>
    <p className="mt-3 font-display text-3xl md:text-4xl font-black tracking-tighter">{value}</p>
  </div>
);

const BreakdownCard = ({ icon, title, items }) => (
  <div className="rounded-3xl bg-white border border-[#A99BE0]/10 p-5">
    <div className="flex items-center gap-2 mb-4">
      {icon}
      <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#574A66]">{title}</p>
    </div>
    {(!items || items.length === 0) ? (
      <p className="text-xs text-[#574A66]">—</p>
    ) : (
      <ul className="space-y-2">
        {items.slice(0, 6).map((it) => (
          <li key={it.key} className="flex items-center justify-between text-sm">
            <span className="text-[#1F1A24] capitalize">{it.key}</span>
            <span className="font-display font-bold text-[#FF7597]">{it.count}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);


const RaffleScheduleCard = ({ schedule, onScheduleTonight, onCustomSubmit, scheduleInput, setScheduleInput, onCancel, onDrawNow, onDrawSpecial, lang }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const scheduledMs = schedule?.scheduled_at ? new Date(schedule.scheduled_at).getTime() : 0;
  const drawn = !!schedule?.drawn;
  const remainingMs = Math.max(0, scheduledMs - now);
  const days = Math.floor(remainingMs / 86400000);
  const hrs = Math.floor((remainingMs % 86400000) / 3600000);
  const mins = Math.floor((remainingMs % 3600000) / 60000);

  const dateLabel = schedule?.scheduled_at
    ? new Date(schedule.scheduled_at).toLocaleString(lang === 'es' ? 'es-CO' : 'en-US', {
        weekday: 'short', day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit', hour12: true,
        timeZone: 'America/Bogota',
      })
    : '';

  return (
    <div className="mt-6 rounded-3xl bg-gradient-to-br from-[#A99BE0] via-[#FF7597] to-[#F4DD8C] p-[2px] shadow-[0_12px_40px_rgba(169,155,224,0.25)]">
      <div className="rounded-[1.4rem] bg-white p-5 md:p-7">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <p className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.3em] uppercase text-[#A99BE0]">
              <Trophy className="w-3.5 h-3.5" />
              {lang === 'es' ? 'SORTEO AUTOMÁTICO' : 'AUTOMATIC RAFFLE'}
            </p>
            <h2 className="mt-2 font-display text-2xl md:text-3xl font-black tracking-tighter text-[#1F1A24]">
              {schedule
                ? drawn
                  ? lang === 'es' ? '¡Sorteo ejecutado!' : 'Raffle drawn!'
                  : lang === 'es' ? 'Sorteo programado' : 'Raffle scheduled'
                : lang === 'es' ? 'Programar sorteo en vivo' : 'Schedule live raffle'}
            </h2>
            {schedule && (
              <p className="mt-2 text-sm text-[#574A66]">
                {lang === 'es' ? 'Fecha y hora · ' : 'Date & time · '}
                <span className="font-bold text-[#1F1A24]">{dateLabel}</span>{' '}
                <span className="opacity-60">(Colombia)</span>
              </p>
            )}
            {schedule && !drawn && remainingMs > 0 && (
              <>
                <p className="mt-2 inline-flex items-center gap-2 text-xs text-[#574A66]">
                  <Clock className="w-3.5 h-3.5 text-[#FF7597]" />
                  {lang === 'es' ? 'En ' : 'In '}
                  <span className="font-display font-black text-[#FF7597] text-base">
                    {days > 0 ? `${days}d ` : ''}{String(hrs).padStart(2, '0')}h {String(mins).padStart(2, '0')}m
                  </span>
                </p>
                <button
                  type="button"
                  onClick={onDrawNow}
                  data-testid="admin-draw-now"
                  className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#FF7597] via-[#FF5C83] to-[#A99BE0] text-white text-xs font-bold tracking-widest uppercase hover:scale-[1.02] transition-transform shadow-[0_8px_24px_rgba(255,117,151,0.35)]"
                >
                  <Trophy className="w-4 h-4" />
                  {lang === 'es' ? '🎲 Sortear ya (sin esperar)' : '🎲 Draw now (skip wait)'}
                </button>
              </>
            )}
            {schedule && drawn && (
              <p className="mt-2 text-xs text-[#1E8A4A] font-bold tracking-widest uppercase">
                {lang === 'es' ? '✓ Ganador(es) seleccionado(s)' : '✓ Winner(s) drawn'}
              </p>
            )}
          </div>

          {schedule && (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1F1A24]/10 text-[#1F1A24] text-[11px] font-bold tracking-widest uppercase hover:bg-[#FF7597] hover:text-white transition-colors self-start"
            >
              <X className="w-3.5 h-3.5" />
              {schedule.drawn
                ? lang === 'es' ? 'Limpiar' : 'Clear'
                : lang === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
          )}
        </div>

        {(!schedule || schedule.drawn) && (
          <div className="mt-5 flex flex-col gap-4">
            {/* PRIMARY ACTION: Sorteo Especial Expobelleza · 6 ganadoras (Día 12 + Día 13) */}
            <button
              type="button"
              onClick={onDrawSpecial}
              data-testid="admin-draw-special"
              className="w-full inline-flex items-center justify-center gap-3 px-6 py-5 rounded-full bg-gradient-to-r from-[#FF2D9C] via-[#FF5C83] to-[#A99BE0] text-white text-base font-black tracking-widest uppercase hover:scale-[1.02] transition-transform shadow-[0_12px_48px_rgba(255,45,156,0.55)] ring-2 ring-[#F4DD8C]/40"
            >
              <Trophy className="w-6 h-6" />
              {lang === 'es' ? '🎊 SORTEO ESPECIAL · 6 GANADORAS' : '🎊 SPECIAL RAFFLE · 6 WINNERS'}
            </button>
            <p className="text-center text-[11px] tracking-widest uppercase text-[#574A66] -mt-2">
              {lang === 'es' ? 'Combinado Día 12 + Día 13 · Expobelleza' : 'Combined Day 12 + Day 13 · Expobelleza'}
            </p>

            {/* Big primary action: draw now */}
            <button
              type="button"
              onClick={onDrawNow}
              data-testid="admin-draw-now"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-gradient-to-r from-[#FF7597] via-[#FF5C83] to-[#A99BE0] text-white text-sm font-bold tracking-widest uppercase hover:scale-[1.02] transition-transform shadow-[0_12px_40px_rgba(255,117,151,0.45)]"
            >
              <Trophy className="w-5 h-5" />
              {lang === 'es' ? '🎲 SORTEAR 1 GANADORA' : '🎲 DRAW 1 WINNER'}
            </button>

            {/* Secondary actions: schedule for later */}
            <div className="flex flex-col md:flex-row gap-3 md:items-end pt-2 border-t border-[#A99BE0]/15">
              <button
                type="button"
                onClick={onScheduleTonight}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#1F1A24] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#FF7597] transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                {lang === 'es' ? 'PROGRAMAR HOY 9:00 PM' : 'SCHEDULE TONIGHT 9:00 PM'}
              </button>

              <span className="text-[10px] tracking-widest uppercase text-[#574A66] md:px-2">
                {lang === 'es' ? 'o personalizado' : 'or custom'}
              </span>

              <form onSubmit={onCustomSubmit} className="flex flex-col sm:flex-row gap-2 flex-1">
                <input
                  type="datetime-local"
                  value={scheduleInput}
                  onChange={(e) => setScheduleInput(e.target.value)}
                  className="flex-1 rounded-2xl border border-[#A99BE0]/30 bg-[#FFFBFB] px-4 py-3 text-sm focus:outline-none focus:border-[#A99BE0] focus:ring-2 focus:ring-[#A99BE0]/20"
                />
                <button
                  type="submit"
                  className="inline-flex items-center justify-center px-5 py-3 rounded-full bg-[#A99BE0] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#7C5DA5] transition-colors"
                >
                  {lang === 'es' ? 'Programar' : 'Schedule'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
